import { ComponentIds, HierarchyUtils, RoomConnectorConstants, TransformUtils, Vector } from '@inter-ikea-core/core';
import SelectedLegComponent from '../../components/SelectedLegComponent';
import { PlannerEcsSystem } from '../System';
import RangeComponent from '../../components/RangeComponent';
/**
 * This system handles adding and removing legs when a frame is added
 * or moved from floor to wall, or wall to floor.
 */
export class LegsSystem extends PlannerEcsSystem {
    constructor(coreApi, defaultLegsSystem, range) {
        super(coreApi, 'LegsSystem', ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
        this.legPartKeys = ['leg-pos-1', 'leg-pos-2', 'leg-pos-3', 'leg-pos-4'];
        /**
         * Returns true if the frame is connected to the floor. Will return correctly
         * if the frames connectors have moved
         */
        this.isFloorStandingFrame = (frame) => {
            const connection = frame.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.highestPriorityConnection;
            return connection && RoomConnectorConstants.hasFloorPlacement(connection);
        };
        this.placementService = coreApi.placementService;
        this.defaultLegProductRef = defaultLegsSystem.DEFAULT_LEG_PRODUCT_REF;
        this.range = range;
    }
    onAdd(entity) {
        /**
         * Handles adding new frames and history redo
         */
        this.updateLegs(entity);
    }
    onUpdate(entity) {
        /**
         * Handles adding/removing legs when frame is move from wall to floor, or
         * from floor to wall
         */
        this.updateLegs(entity);
    }
    onRemove() { }
    /**
     * Add legs to floor standing frames and remove legs from wall hanging frames
     */
    updateLegs(entity) {
        if (!entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID) ||
            !this.isEnabled ||
            (this.range && this.range !== entity.getComponent(RangeComponent.id)?.range)) {
            return;
        }
        if (this.isFloorStandingFrame(entity)) {
            this.addLegsOnFrame(entity, this.getCurrentSelectedLeg());
        }
        else if (this.isWallHangedTooLow(entity)) {
            this.resnapFrame(entity, new Vector(0, 100, 0));
        }
        else if (!this.isFloorStandingFrame(entity) && this.hasLegs(entity)) {
            this.removeLegsFromFrame(entity);
        }
    }
    /**
     * Adds legs to the provided frame. If the frame has legs
     * they are replaced only if productRef has changed
     */
    addLegsOnFrame(frame, productRef) {
        const currentLegs = this.getLegs(frame);
        if (currentLegs.length === 0) {
            this.legPartKeys.forEach(() => {
                this.addToTarget(productRef, frame);
            });
        }
        else if (productRef !== this.getCurrentSelectedLeg()) {
            this.replaceLegs(frame, productRef);
        }
    }
    /**
     * Removes legs from frame
     */
    removeLegsFromFrame(frame) {
        const legs = this.getLegs(frame);
        if (legs.length > 0) {
            legs.forEach(e => {
                this.coreApi.entities.delete(e);
            });
        }
    }
    /**
     * Returns the current selected leg product ref
     */
    getCurrentSelectedLeg() {
        const leg = this.coreApi.entities
            .getScene()
            ?.getComponent(SelectedLegComponent.id);
        return leg?.coreProductId || this.defaultLegProductRef;
    }
    /**
     * Returns the legs attached to the frame
     */
    getLegs(frame) {
        return HierarchyUtils.filterInHierarchy(frame, e => e.hasComponent(ComponentIds.LEG_COMPONENT_ID));
    }
    /**
     * Returns true if the frame has legs
     */
    hasLegs(frame) {
        return this.getLegs(frame).length > 0;
    }
    /**
     * Since we need to add and replace entities without adding history
     * checkpoints, we cannot use the ProductApi. Instead we have an implementation
     * of add to target here as well
     */
    addToTarget(productRef, target) {
        const entity = this.coreApi.getServices().productManager.createEntity(productRef);
        if (entity) {
            this.placementService.forceSnapEntity(entity, target);
        }
    }
    /**
     * Since we need to add and replace entities without adding history
     * checkpoints, we cannot use the ProductApi. Instead we have an implementation
     * of replace here as well
     */
    replaceLegs(frame, productRef) {
        if (this.range && this.range !== frame.getComponent(RangeComponent.id)?.range) {
            return;
        }
        this.disable();
        const currentLegs = this.getLegs(frame);
        currentLegs.forEach(leg => this.coreApi.entities.delete(leg));
        const placementResult = this.resnapFrame(frame);
        this.legPartKeys.forEach(() => {
            this.addToTarget(productRef, frame);
        });
        this.enable();
        return placementResult;
    }
    /**
     * Resnaps the frame at it's current position (with optional offset)
     */
    resnapFrame(frame, offset = new Vector(0, 0, 0)) {
        const position = TransformUtils.worldPosition(frame);
        return this.coreApi.placementService.snapToPosition(frame, position.plus(offset), true);
    }
    /**
     * Chech if frame is wall hanged below 10cm.
     */
    isWallHangedTooLow(frame) {
        if (this.isFloorStandingFrame(frame)) {
            return false;
        }
        const position = TransformUtils.worldPosition(frame);
        const size = this.coreApi.entities.getEntityComSize(frame);
        const bottomOfFramePosition = size && position.Y - size.height / 2;
        return bottomOfFramePosition !== undefined && bottomOfFramePosition <= 100;
    }
    /**
     * Public function which can be called by planner to set legs on frames.
     * Calling this will create a history checkpoint
     */
    updateLegsWithHistory(productRef) {
        let replaceResult = [];
        this.coreApi.history.historyAction(() => {
            const scene = this.coreApi.getServices().sceneManager.scene;
            if (scene) {
                scene.addComponent(new SelectedLegComponent(productRef));
            }
            const floorstandingFrames = this.coreApi.entities
                .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
                .filter(frame => this.isFloorStandingFrame(frame));
            this.coreApi.validations['WallVisibilityValidation']?.disable();
            replaceResult = floorstandingFrames.map(frame => this.replaceLegs(frame, productRef));
            this.coreApi.validations['WallVisibilityValidation']?.enable();
        });
        if (replaceResult.some(result => result?.valid === false)) {
            // If not possible to resnap to new position (e.g. colliding with roof or hanging frames) we undo leg change
            this.coreApi.history.undo();
            this.coreApi.history.discardLatestSnapshot();
        }
        return replaceResult;
    }
}
//# sourceMappingURL=LegsSystem.js.map